/**
 * Kickard PHASE 3.9A — Admin Analytics Dashboard.
 *
 * Intentionally-simple internal dashboard. Not linked from any player-facing
 * navigation. Access requires the ADMIN_ANALYTICS_KEY (backend env var)
 * entered by the operator; stored in sessionStorage for the tab lifetime.
 *
 * Sections (tabs):
 *   1. Overview
 *   2. Players
 *   3. Sessions
 *   4. Quick Match
 *   5. Retention
 *   6. Recent Events
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KEY_STORAGE = 'kickard.admin.analytics.key.v1';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function fmtDuration(seconds) {
  if (seconds == null) return '—';
  const s = Math.round(Number(seconds) || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtPct(v) {
  if (v == null) return '—';
  return `${(Number(v) * 100).toFixed(1)}%`;
}

function fmtNum(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString();
}

async function adminFetch(path, key) {
  const res = await fetch(`${BACKEND}${path}`, {
    headers: { 'X-Kickard-Admin-Key': key },
  });
  if (res.status === 401) throw new Error('MISSING_KEY');
  if (res.status === 403) throw new Error('INVALID_KEY');
  if (res.status === 503) throw new Error('ADMIN_DISABLED');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
}

// ============================================================
// KEY GATE
// ============================================================
function KeyGate({ onUnlock }) {
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function trySubmit() {
    if (!input.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await adminFetch('/api/admin/analytics/overview', input.trim());
      try { sessionStorage.setItem(KEY_STORAGE, input.trim()); } catch (_) { /* ignore */ }
      onUnlock(input.trim());
    } catch (e) {
      if (String(e.message).includes('INVALID_KEY') || String(e.message).includes('MISSING_KEY')) {
        setError('Invalid admin key.');
      } else if (String(e.message).includes('ADMIN_DISABLED')) {
        setError('Admin analytics disabled: ADMIN_ANALYTICS_KEY not configured on server.');
      } else {
        setError(String(e.message || e));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="admin-key-gate" className="min-h-[100dvh] w-full flex items-center justify-center bg-black text-white px-6">
      <div className="w-full max-w-sm space-y-5">
        <div>
          <h1 className="font-heading text-2xl tracking-[0.3em] text-[hsl(var(--k-gold-bright))]">KICKARD · ADMIN</h1>
          <p className="text-white/55 text-sm mt-1">Analytics dashboard (internal).</p>
        </div>
        <div className="space-y-2">
          <label className="text-white/65 text-xs uppercase tracking-[0.28em]">Admin Key</label>
          <input
            data-testid="admin-key-input"
            type="password"
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') trySubmit(); }}
            className="w-full px-3 py-2.5 rounded bg-white/5 border border-white/15 text-white font-mono outline-none focus:border-[hsl(var(--k-gold))]"
            placeholder="Paste ADMIN_ANALYTICS_KEY"
          />
        </div>
        {error && <div data-testid="admin-key-error" className="text-[hsl(var(--k-red))] text-sm">{error}</div>}
        <button
          data-testid="admin-key-submit"
          disabled={busy || !input.trim()}
          onClick={trySubmit}
          className="w-full py-2.5 rounded bg-[hsl(var(--k-gold))] text-black font-heading tracking-[0.22em] disabled:opacity-50"
        >
          {busy ? 'CHECKING…' : 'UNLOCK'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// SHARED
// ============================================================
function Card({ label, value, testid, sub }) {
  return (
    <div data-testid={testid} className="p-4 rounded-lg bg-white/[0.04] border border-white/10">
      <div className="text-white/50 text-[10px] uppercase tracking-[0.24em]">{label}</div>
      <div className="text-white text-2xl font-mono mt-1">{value ?? '—'}</div>
      {sub && <div className="text-white/40 text-[10px] mt-1">{sub}</div>}
    </div>
  );
}

function TabBar({ current, onChange }) {
  const tabs = [
    ['overview', 'Overview'],
    ['players', 'Players'],
    ['sessions', 'Sessions'],
    ['quick-match', 'Quick Match'],
    ['retention', 'Retention'],
    ['recent-events', 'Recent Events'],
    ['trust', 'Trust'],
    ['ranked', 'Ranked'],
  ];
  return (
    <div data-testid="admin-tabs" className="flex flex-wrap gap-2 mb-4">
      {tabs.map(([id, label]) => (
        <button
          key={id}
          data-testid={`admin-tab-${id}`}
          onClick={() => onChange(id)}
          className={`px-3 py-1.5 rounded font-heading text-[11px] tracking-[0.22em] ${
            current === id
              ? 'bg-[hsl(var(--k-gold))] text-black'
              : 'bg-white/[0.06] text-white/70 hover:bg-white/[0.1]'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ============================================================
// TABS
// ============================================================
function OverviewTab({ apiKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [rebuildBusy, setRebuildBusy] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileResult, setProfileResult] = useState(null);

  const load = useCallback(() => {
    adminFetch('/api/admin/analytics/overview', apiKey).then(setData).catch((e) => setErr(String(e.message)));
  }, [apiKey]);
  useEffect(() => { load(); }, [load]);

  async function rebuild() {
    if (rebuildBusy) return;
    if (!window.confirm('Rebuild player_analytics totals from match_history? This resets match counters and recomputes them from every historical match. Session totals are preserved.')) return;
    setRebuildBusy(true);
    setRebuildResult(null);
    try {
      const res = await fetch(`${BACKEND}/api/admin/analytics/rebuild-player-analytics`, {
        method: 'POST', headers: { 'X-Kickard-Admin-Key': apiKey },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRebuildResult(await res.json());
      load();
    } catch (e) { setRebuildResult({ error: String(e.message || e) }); }
    finally { setRebuildBusy(false); }
  }

  async function sweep() {
    if (sweepBusy) return;
    setSweepBusy(true);
    setSweepResult(null);
    try {
      const res = await fetch(`${BACKEND}/api/admin/analytics/sweep-stale-sessions`, {
        method: 'POST', headers: { 'X-Kickard-Admin-Key': apiKey },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSweepResult(await res.json());
      load();
    } catch (e) { setSweepResult({ error: String(e.message || e) }); }
    finally { setSweepBusy(false); }
  }

  async function rebuildProfile() {
    if (profileBusy) return;
    if (!window.confirm('Rebuild player_profile_stats from match_history? This is idempotent and does not touch match outcomes, trust signals, or sessions.')) return;
    setProfileBusy(true);
    setProfileResult(null);
    try {
      const res = await fetch(`${BACKEND}/api/admin/analytics/rebuild-profile-stats`, {
        method: 'POST', headers: { 'X-Kickard-Admin-Key': apiKey },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setProfileResult(await res.json());
    } catch (e) { setProfileResult({ error: String(e.message || e) }); }
    finally { setProfileBusy(false); }
  }

  if (err) return <div className="text-[hsl(var(--k-red))]">{err}</div>;
  if (!data) return <div className="text-white/50">Loading…</div>;
  return (
    <div data-testid="admin-overview" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card testid="ov-active-today" label="Active Today" value={fmtNum(data.activeToday)} />
        <Card testid="ov-active-7" label="Active (7d)" value={fmtNum(data.activeLast7Days)} />
        <Card testid="ov-active-30" label="Active (30d)" value={fmtNum(data.activeLast30Days)} />
        <Card testid="ov-total-players" label="Total Players" value={fmtNum(data.totalPlayers)} sub={`+${data.newPlayersToday ?? 0} today`} />
        <Card testid="ov-total-sessions" label="Total Sessions" value={fmtNum(data.totalSessions)} />
        <Card testid="ov-total-matches" label="Total Matches" value={fmtNum(data.totalMatches)} sub={`${data.matchesToday ?? 0} today`} />
        <Card testid="ov-completion-rate" label="Match Completion" value={fmtPct(data.matchCompletionRate)} />
        <Card testid="ov-avg-duration" label="Avg Match Duration" value={fmtDuration(data.averageMatchDurationSeconds)} />
        <Card testid="ov-quick-wait" label="QM Avg Wait" value={fmtDuration(data.quickMatchAverageWaitSeconds)} />
        <Card testid="ov-quick-cancel" label="QM Cancel Rate" value={fmtPct(data.quickMatchCancelRate)} />
        <Card testid="ov-quick-complete" label="QM Completion Rate" value={fmtPct(data.quickMatchCompletionRate)} />
      </div>
      <div data-testid="ov-top-players">
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">TOP ACTIVE PLAYERS</h3>
        <table className="w-full text-sm">
          <thead className="text-white/40 text-[10px] uppercase tracking-[0.24em]">
            <tr>
              <th className="text-left py-1">Player</th>
              <th className="text-right py-1">Matches Completed</th>
              <th className="text-right py-1">Playtime</th>
              <th className="text-right py-1">Last Seen</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {(data.topActivePlayers || []).map((p) => (
              <tr key={p.guestId} className="border-t border-white/5">
                <td className="py-1.5">{p.displayName || p.guestId?.slice(0, 10)}</td>
                <td className="py-1.5 text-right">{fmtNum(p.totalMatchesCompleted)}</td>
                <td className="py-1.5 text-right">{fmtDuration(p.totalPlaytimeSeconds)}</td>
                <td className="py-1.5 text-right">{fmtDate(p.lastSeenAt)}</td>
              </tr>
            ))}
            {(!data.topActivePlayers || data.topActivePlayers.length === 0) && (
              <tr><td colSpan={4} className="text-white/40 py-2 italic">No completed matches yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Phase 3.9A.1 — Admin Tools */}
      <div data-testid="ov-admin-tools" className="pt-4 border-t border-white/10">
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">ADMIN TOOLS</h3>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="ov-rebuild-btn"
            onClick={rebuild}
            disabled={rebuildBusy}
            className="px-3 py-1.5 rounded bg-white/[0.06] text-white/80 text-sm hover:bg-white/[0.1] disabled:opacity-50"
          >
            {rebuildBusy ? 'Rebuilding…' : 'Rebuild Analytics from match_history'}
          </button>
          <button
            data-testid="ov-sweep-btn"
            onClick={sweep}
            disabled={sweepBusy}
            className="px-3 py-1.5 rounded bg-white/[0.06] text-white/80 text-sm hover:bg-white/[0.1] disabled:opacity-50"
          >
            {sweepBusy ? 'Sweeping…' : 'Sweep Stale Sessions (3m)'}
          </button>
          <button
            data-testid="ov-rebuild-profile-btn"
            onClick={rebuildProfile}
            disabled={profileBusy}
            className="px-3 py-1.5 rounded bg-white/[0.06] text-white/80 text-sm hover:bg-white/[0.1] disabled:opacity-50"
          >
            {profileBusy ? 'Rebuilding…' : 'Rebuild Profile Stats'}
          </button>
        </div>
        {rebuildResult && (
          <div data-testid="ov-rebuild-result" className="mt-2 text-white/60 text-xs font-mono">
            {rebuildResult.error
              ? <span className="text-[hsl(var(--k-red))]">Error: {rebuildResult.error}</span>
              : <span>Rebuilt: {rebuildResult.matchesProcessed} matches processed, {rebuildResult.playersTouched} players touched. {rebuildResult.note}</span>}
          </div>
        )}
        {sweepResult && (
          <div data-testid="ov-sweep-result" className="mt-2 text-white/60 text-xs font-mono">
            {sweepResult.error
              ? <span className="text-[hsl(var(--k-red))]">Error: {sweepResult.error}</span>
              : <span>Closed {sweepResult.closedSessions} stale session(s) (cutoff={sweepResult.cutoffSeconds}s).</span>}
          </div>
        )}
        {profileResult && (
          <div data-testid="ov-rebuild-profile-result" className="mt-2 text-white/60 text-xs font-mono">
            {profileResult.error
              ? <span className="text-[hsl(var(--k-red))]">Error: {profileResult.error}</span>
              : <span>Rebuilt profile stats: {profileResult.matchesProcessed} matches / {profileResult.playersTouched} players / {profileResult.h2hPairsProcessed} H2H pairs.</span>}
          </div>
        )}
      </div>
      <div data-testid="ov-generated-at" className="text-white/30 text-[10px]">Generated: {fmtDate(data.generatedAt)}</div>
    </div>
  );
}

function PlayersTab({ apiKey, onOpenPlayer }) {
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState('lastSeenAt');
  const [search, setSearch] = useState('');
  const [err, setErr] = useState(null);

  const fetchNow = useCallback(async () => {
    setErr(null);
    try {
      const params = new URLSearchParams({ sort, limit: '100' });
      if (search) params.set('search', search);
      const data = await adminFetch(`/api/admin/analytics/players?${params}`, apiKey);
      setRows(data.players || []);
    } catch (e) { setErr(String(e.message)); }
  }, [apiKey, sort, search]);
  useEffect(() => { fetchNow(); }, [fetchNow]);

  return (
    <div data-testid="admin-players" className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <input
          data-testid="players-search"
          placeholder="Search by display name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-2 py-1.5 bg-white/5 border border-white/10 rounded text-white text-sm"
        />
        <select
          data-testid="players-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="px-2 py-1.5 bg-white/5 border border-white/10 rounded text-white text-sm"
        >
          <option value="lastSeenAt">Last seen</option>
          <option value="totalPlaytimeSeconds">Playtime</option>
          <option value="totalMatchesCompleted">Matches completed</option>
          <option value="matchCompletionRate">Completion rate</option>
        </select>
        <button onClick={fetchNow} className="px-2 py-1.5 rounded bg-white/[0.06] text-white/80 text-sm">Refresh</button>
      </div>
      {err && <div className="text-[hsl(var(--k-red))]">{err}</div>}
      <table className="w-full text-sm">
        <thead className="text-white/40 text-[10px] uppercase tracking-[0.24em]">
          <tr>
            <th className="text-left py-1">Display Name</th>
            <th className="text-right py-1">Last Seen</th>
            <th className="text-right py-1">Playtime</th>
            <th className="text-right py-1">Sessions</th>
            <th className="text-right py-1">Matches ✓</th>
            <th className="text-right py-1">Completion</th>
            <th className="text-right py-1">Preferred</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rows.map((p) => (
            <tr
              key={p.guestId}
              data-testid={`player-row-${p.guestId}`}
              onClick={() => onOpenPlayer(p.guestId)}
              className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer"
            >
              <td className="py-1.5">{p.displayName || <span className="text-white/40">{p.guestId?.slice(0, 10)}</span>}</td>
              <td className="py-1.5 text-right">{fmtDate(p.lastSeenAt)}</td>
              <td className="py-1.5 text-right">{fmtDuration(p.totalPlaytimeSeconds)}</td>
              <td className="py-1.5 text-right">{fmtNum(p.totalSessions)}</td>
              <td className="py-1.5 text-right">{fmtNum(p.totalMatchesCompleted)}</td>
              <td className="py-1.5 text-right">{fmtPct(p.matchCompletionRate)}</td>
              <td className="py-1.5 text-right text-white/70">{p.preferredMode || '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && (<tr><td colSpan={7} className="text-white/40 py-3 italic">No players yet — waiting for the first session.</td></tr>)}
        </tbody>
      </table>
      <div data-testid="players-generated-at" className="text-white/30 text-[10px]">Loaded: {new Date().toLocaleString()}</div>
    </div>
  );
}

function PlayerDetail({ apiKey, guestId, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    adminFetch(`/api/admin/analytics/player/${encodeURIComponent(guestId)}`, apiKey)
      .then(setData).catch((e) => setErr(String(e.message)));
  }, [apiKey, guestId]);
  if (err) return <div className="text-[hsl(var(--k-red))]">{err} <button onClick={onBack} className="underline ml-2">Back</button></div>;
  if (!data) return <div className="text-white/50">Loading…</div>;
  const p = data.player;
  return (
    <div data-testid="admin-player-detail" className="space-y-4">
      <button onClick={onBack} className="text-white/60 text-sm">← Back to players</button>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Display Name" value={p.displayName || '—'} />
        <Card label="Matches ✓" value={fmtNum(p.totalMatchesCompleted)} />
        <Card label="Completion" value={fmtPct(p.matchCompletionRate)} />
        <Card label="Playtime" value={fmtDuration(p.totalPlaytimeSeconds)} />
        <Card label="Sessions" value={fmtNum(p.totalSessions)} />
        <Card label="Avg Session" value={fmtDuration(p.averageSessionLengthSeconds)} />
        <Card label="First Seen" value={fmtDate(p.firstSeenAt)} />
        <Card label="Last Seen" value={fmtDate(p.lastSeenAt)} />
      </div>
      <div>
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">MODE SPLIT</h3>
        <div className="grid grid-cols-3 gap-3 text-sm font-mono">
          {['friend', 'quick_casual', 'bot_practice'].map((mt) => (
            <div key={mt} className="p-3 rounded bg-white/[0.04] border border-white/10">
              <div className="text-white/50 text-[10px] uppercase tracking-[0.22em]">{mt}</div>
              <div className="text-white">Started: {p.modeCounts?.[mt]?.started ?? 0}</div>
              <div className="text-white">Completed: {p.modeCounts?.[mt]?.completed ?? 0}</div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">RECENT SESSIONS</h3>
        <table className="w-full text-sm">
          <thead className="text-white/40 text-[10px] uppercase tracking-[0.22em]">
            <tr><th className="text-left py-1">Started</th><th className="text-right py-1">Duration</th><th className="text-right py-1">Matches</th><th className="text-right py-1">Screen</th></tr>
          </thead>
          <tbody className="font-mono">
            {(data.recentSessions || []).map((s) => (
              <tr key={s.sessionId} className="border-t border-white/5">
                <td className="py-1">{fmtDate(s.startedAt)}</td>
                <td className="py-1 text-right">{fmtDuration(s.durationSeconds)}</td>
                <td className="py-1 text-right">{s.matchesStarted}/{s.matchesCompleted}</td>
                <td className="py-1 text-right text-white/60">{s.lastScreen || '—'}</td>
              </tr>
            ))}
            {(!data.recentSessions?.length) && (<tr><td colSpan={4} className="text-white/40 py-2">No sessions yet.</td></tr>)}
          </tbody>
        </table>
      </div>
      <div>
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">RECENT MATCHES</h3>
        <table className="w-full text-sm">
          <thead className="text-white/40 text-[10px] uppercase tracking-[0.22em]">
            <tr><th className="text-left py-1">Completed</th><th className="text-left py-1">Type</th><th className="text-left py-1">Result</th><th className="text-right py-1">Duration</th></tr>
          </thead>
          <tbody className="font-mono">
            {(data.recentMatches || []).map((m) => (
              <tr key={m.matchId} className="border-t border-white/5">
                <td className="py-1">{fmtDate(m.completedAt)}</td>
                <td className="py-1">{m.matchType}</td>
                <td className="py-1">{m.status} · {m.endedBy}</td>
                <td className="py-1 text-right">{fmtDuration(m.durationSeconds)}</td>
              </tr>
            ))}
            {(!data.recentMatches?.length) && (<tr><td colSpan={4} className="text-white/40 py-2">No completed matches.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SessionsTab({ apiKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    adminFetch('/api/admin/analytics/sessions?limit=200', apiKey).then(setData).catch((e) => setErr(String(e.message)));
  }, [apiKey]);
  if (err) return <div className="text-[hsl(var(--k-red))]">{err}</div>;
  if (!data) return <div className="text-white/50">Loading…</div>;
  return (
    <div data-testid="admin-sessions" className="space-y-3">
      <div className="text-white/50 text-sm">{data.count} recent sessions</div>
      <table className="w-full text-sm">
        <thead className="text-white/40 text-[10px] uppercase tracking-[0.22em]">
          <tr>
            <th className="text-left py-1">Player</th>
            <th className="text-left py-1">Started</th>
            <th className="text-right py-1">Duration</th>
            <th className="text-right py-1">Last Seen</th>
            <th className="text-right py-1">Matches</th>
            <th className="text-right py-1">Events</th>
            <th className="text-right py-1">Screen</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {(data.sessions || []).map((s) => (
            <tr key={s.sessionId} className="border-t border-white/5">
              <td className="py-1">{s.displayName || <span className="text-white/40">{s.guestId?.slice(0, 10)}</span>}</td>
              <td className="py-1">{fmtDate(s.startedAt)}</td>
              <td className="py-1 text-right">{fmtDuration(s.durationSeconds)}</td>
              <td className="py-1 text-right">{fmtDate(s.endedAt || s.lastHeartbeatAt)}</td>
              <td className="py-1 text-right">{s.matchesStarted}/{s.matchesCompleted}</td>
              <td className="py-1 text-right">{s.eventsCount}</td>
              <td className="py-1 text-right text-white/60">{s.lastScreen || '—'}</td>
            </tr>
          ))}
          {(!data.sessions || data.sessions.length === 0) && (
            <tr><td colSpan={7} className="text-white/40 py-3 italic">No sessions yet.</td></tr>
          )}
        </tbody>
      </table>
      <div data-testid="sessions-generated-at" className="text-white/30 text-[10px]">Loaded: {new Date().toLocaleString()}</div>
    </div>
  );
}

function QuickMatchTab({ apiKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    adminFetch('/api/admin/analytics/quick-match', apiKey).then(setData).catch((e) => setErr(String(e.message)));
  }, [apiKey]);
  if (err) return <div className="text-[hsl(var(--k-red))]">{err}</div>;
  if (!data) return <div className="text-white/50">Loading…</div>;
  return (
    <div data-testid="admin-quick-match" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card testid="qm-joins" label="Queue Joins" value={fmtNum(data.queueJoins)} />
        <Card testid="qm-matched" label="Matched" value={fmtNum(data.matched)} />
        <Card testid="qm-cancelled" label="Cancelled" value={fmtNum(data.cancelled)} />
        <Card testid="qm-expired" label="Expired" value={fmtNum(data.expired)} />
        <Card
          testid="qm-avg-wait"
          label="Avg Wait"
          value={fmtDuration(data.averageWaitSeconds)}
          sub={data.averageWaitSampleSize > 0 ? `across ${data.averageWaitSampleSize} matches with wait data` : 'no matches with wait data yet'}
        />
        <Card testid="qm-cancel-rate" label="Cancel Rate" value={fmtPct(data.cancelRate)} />
        <Card testid="qm-completion" label="Completion After Match" value={fmtPct(data.completionRateAfterMatch)} />
      </div>
      <div>
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">RECENT QUICK-MATCH EVENTS</h3>
        <table className="w-full text-sm">
          <thead className="text-white/40 text-[10px] uppercase tracking-[0.22em]">
            <tr><th className="text-left py-1">Time</th><th className="text-left py-1">Event</th><th className="text-left py-1">Player</th><th className="text-left py-1">Meta</th></tr>
          </thead>
          <tbody className="font-mono">
            {(data.recentRecords || []).map((e, i) => (
              <tr key={i} className="border-t border-white/5">
                <td className="py-1">{fmtDate(e.timestamp)}</td>
                <td className="py-1">{e.eventName}</td>
                <td className="py-1">{e.displayName || <span className="text-white/40">{e.guestId?.slice(0, 10)}</span>}</td>
                <td className="py-1 text-white/60 truncate max-w-[240px]">{JSON.stringify(e.metadata || {})}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div data-testid="qm-generated-at" className="text-white/30 text-[10px]">Loaded: {new Date().toLocaleString()}</div>
    </div>
  );
}

function RetentionTab({ apiKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    adminFetch('/api/admin/analytics/retention', apiKey).then(setData).catch((e) => setErr(String(e.message)));
  }, [apiKey]);
  if (err) return <div className="text-[hsl(var(--k-red))]">{err}</div>;
  if (!data) return <div className="text-white/50">Loading…</div>;
  return (
    <div data-testid="admin-retention" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card testid="ret-dau" label="DAU" value={fmtNum(data.dau)} />
        <Card testid="ret-wau" label="WAU" value={fmtNum(data.wau)} />
        <Card testid="ret-mau" label="MAU" value={fmtNum(data.mau)} />
        <Card testid="ret-new-today" label="New Today" value={fmtNum(data.newPlayersToday)} />
        <Card testid="ret-returning-today" label="Returning Today" value={fmtNum(data.returningPlayersToday)} />
        <Card testid="ret-d1" label="D1 Retention" value={fmtPct(data.d1)} />
        <Card testid="ret-d3" label="D3 Retention" value={fmtPct(data.d3)} />
        <Card testid="ret-d7" label="D7 Retention" value={fmtPct(data.d7)} />
        <Card testid="ret-first-match" label="First Match ✓" value={fmtPct(data.firstMatchCompletionRate)} sub="Players completed ≥1 match" />
        <Card testid="ret-return-match" label="Returned After First Match" value={fmtNum(data.returnedAfterFirstMatch)} />
        <Card testid="ret-return-win" label="Returned After First Win" value={fmtNum(data.returnedAfterFirstWin)} />
        <Card testid="ret-return-loss" label="Returned After First Loss" value={fmtNum(data.returnedAfterFirstLoss)} />
      </div>
      <div className="text-white/30 text-[10px]">Generated: {fmtDate(data.generatedAt)}</div>
    </div>
  );
}

function RecentEventsTab({ apiKey }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [filterEvent, setFilterEvent] = useState('');
  const [filterGuest, setFilterGuest] = useState('');
  const [filterType, setFilterType] = useState('');

  const fetchNow = useCallback(async () => {
    setErr(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filterEvent) params.set('eventName', filterEvent);
      if (filterGuest) params.set('guestId', filterGuest);
      if (filterType) params.set('matchType', filterType);
      const data = await adminFetch(`/api/admin/analytics/recent-events?${params}`, apiKey);
      setRows(data.events || []);
    } catch (e) { setErr(String(e.message)); }
  }, [apiKey, filterEvent, filterGuest, filterType]);
  useEffect(() => { fetchNow(); }, [fetchNow]);

  return (
    <div data-testid="admin-recent-events" className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input placeholder="eventName" value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} className="px-2 py-1.5 bg-white/5 border border-white/10 rounded text-white text-sm" data-testid="events-filter-event" />
        <input placeholder="guestId" value={filterGuest} onChange={(e) => setFilterGuest(e.target.value)} className="px-2 py-1.5 bg-white/5 border border-white/10 rounded text-white text-sm" data-testid="events-filter-guest" />
        <input placeholder="matchType" value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-2 py-1.5 bg-white/5 border border-white/10 rounded text-white text-sm" data-testid="events-filter-type" />
        <button onClick={fetchNow} className="px-2 py-1.5 rounded bg-white/[0.06] text-white/80 text-sm">Refresh</button>
      </div>
      {err && <div className="text-[hsl(var(--k-red))]">{err}</div>}
      <table className="w-full text-sm">
        <thead className="text-white/40 text-[10px] uppercase tracking-[0.22em]">
          <tr>
            <th className="text-left py-1">Time</th>
            <th className="text-left py-1">Event</th>
            <th className="text-left py-1">Player</th>
            <th className="text-left py-1">Type</th>
            <th className="text-left py-1">Screen</th>
            <th className="text-left py-1">Metadata</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rows.map((e, i) => (
            <tr key={i} className="border-t border-white/5">
              <td className="py-1">{fmtDate(e.timestamp)}</td>
              <td className="py-1">{e.eventName}</td>
              <td className="py-1">{e.displayName || <span className="text-white/40">{e.guestId?.slice(0, 10)}</span>}</td>
              <td className="py-1">{e.matchType || '—'}</td>
              <td className="py-1 text-white/60">{e.metadata?.sourceScreen || '—'}</td>
              <td className="py-1 text-white/60 truncate max-w-[280px]">{JSON.stringify(e.metadata || {})}</td>
            </tr>
          ))}
          {rows.length === 0 && (<tr><td colSpan={6} className="text-white/40 py-3 italic">No recent events matching filters.</td></tr>)}
        </tbody>
      </table>
      <div data-testid="events-generated-at" className="text-white/30 text-[10px]">Loaded: {new Date().toLocaleString()}</div>
    </div>
  );
}

// ============================================================
// PHASE 3.9B — TRUST TAB
// ============================================================
function TrustTab({ apiKey }) {
  const [overview, setOverview] = useState(null);
  const [pairs, setPairs] = useState(null);
  const [err, setErr] = useState(null);
  const [rebuildBusy, setRebuildBusy] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [ov, pr] = await Promise.all([
        adminFetch('/api/admin/analytics/trust', apiKey),
        adminFetch('/api/admin/analytics/trust/repeated-opponents?limit=50', apiKey),
      ]);
      setOverview(ov);
      setPairs(pr);
    } catch (e) { setErr(String(e.message)); }
  }, [apiKey]);
  useEffect(() => { load(); }, [load]);

  async function rebuild() {
    if (rebuildBusy) return;
    if (!window.confirm('Rebuild player_trust_signals + repeated_opponent_pairs from match_history + telemetry? This resets and recomputes all trust aggregates. It does not touch match outcomes or player accounts.')) return;
    setRebuildBusy(true);
    setRebuildResult(null);
    try {
      const res = await fetch(`${BACKEND}/api/admin/analytics/rebuild-trust-signals`, {
        method: 'POST', headers: { 'X-Kickard-Admin-Key': apiKey },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRebuildResult(await res.json());
      await load();
    } catch (e) { setRebuildResult({ error: String(e.message || e) }); }
    finally { setRebuildBusy(false); }
  }

  if (err) return <div className="text-[hsl(var(--k-red))]">{err}</div>;
  if (!overview || !pairs) return <div className="text-white/50">Loading…</div>;

  return (
    <div data-testid="admin-trust" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card testid="trust-total-flagged" label="Flagged Players" value={fmtNum(overview.totalFlaggedPlayers)} />
        <Card testid="trust-repeated-count" label="Repeated Opponent Signals" value={fmtNum(overview.repeatedOpponentFlagsCount)} />
        <Card testid="trust-low-completion" label="Low Completion" value={fmtNum(overview.lowCompletionFlagsCount)} />
        <Card testid="trust-high-cancel" label="High Queue Cancel" value={fmtNum(overview.highQueueCancelFlagsCount)} />
        <Card testid="trust-high-disc" label="High Disconnect" value={fmtNum(overview.highDisconnectFlagsCount)} />
        <Card testid="trust-high-timeout" label="High Timeout" value={fmtNum(overview.highTimeoutFlagsCount)} />
        <Card testid="trust-high-forfeit" label="High Forfeit" value={fmtNum(overview.highForfeitFlagsCount)} />
      </div>

      {/* Top Risk Players */}
      <div data-testid="trust-top-risk">
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">TOP RISK PLAYERS · Needs Review</h3>
        <table className="w-full text-sm">
          <thead className="text-white/40 text-[10px] uppercase tracking-[0.22em]">
            <tr>
              <th className="text-left py-1">Player</th>
              <th className="text-right py-1">Risk Score</th>
              <th className="text-right py-1">Completion</th>
              <th className="text-right py-1">Cancel Rate</th>
              <th className="text-right py-1">Disc. 7d</th>
              <th className="text-right py-1">Timeout 7d</th>
              <th className="text-right py-1">Forfeit 7d</th>
              <th className="text-right py-1">Flags</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {(overview.topRiskPlayers || []).map((p) => (
              <tr key={p.guestId} data-testid={`trust-row-${p.guestId}`} className="border-t border-white/5">
                <td className="py-1.5">{p.displayName || <span className="text-white/40">{p.guestId?.slice(0, 10)}</span>}</td>
                <td className="py-1.5 text-right">{p.riskScore}</td>
                <td className="py-1.5 text-right">{fmtPct(p.matchCompletionRate)}</td>
                <td className="py-1.5 text-right">{fmtPct(p.quickMatchCancelRate)}</td>
                <td className="py-1.5 text-right">{p.disconnectCount7d ?? 0}</td>
                <td className="py-1.5 text-right">{p.timeoutStrikeCount7d ?? 0}</td>
                <td className="py-1.5 text-right">{p.forfeitCount7d ?? 0}</td>
                <td className="py-1.5 text-right text-white/60">
                  {['lowCompletionRateFlag', 'highQueueCancelRateFlag', 'highDisconnectRateFlag', 'highTimeoutStrikeFlag', 'highForfeitFlag', 'suspiciousRepeatedOpponentFlag']
                    .filter((k) => p[k]).map((k) => k.replace('Flag', '').replace('Rate', '')).join(', ') || '—'}
                </td>
              </tr>
            ))}
            {(!overview.topRiskPlayers?.length) && (
              <tr><td colSpan={8} className="text-white/40 py-2 italic">No flagged players — good news.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Repeated Opponent Pairs */}
      <div data-testid="trust-pairs">
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">REPEATED OPPONENT PAIRS</h3>
        <table className="w-full text-sm">
          <thead className="text-white/40 text-[10px] uppercase tracking-[0.22em]">
            <tr>
              <th className="text-left py-1">Player A</th>
              <th className="text-left py-1">Player B</th>
              <th className="text-right py-1">24h</th>
              <th className="text-right py-1">7d</th>
              <th className="text-right py-1">Quick 7d</th>
              <th className="text-right py-1">Friend 7d</th>
              <th className="text-right py-1">Last Match</th>
              <th className="text-right py-1">Signal</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {(pairs.pairs || []).map((p) => (
              <tr key={p.pairKey} className="border-t border-white/5">
                <td className="py-1">{p.displayNameA || <span className="text-white/40">{p.guestIdA?.slice(0, 10)}</span>}</td>
                <td className="py-1">{p.displayNameB || <span className="text-white/40">{p.guestIdB?.slice(0, 10)}</span>}</td>
                <td className="py-1 text-right">{fmtNum(p.matches24h)}</td>
                <td className="py-1 text-right">{fmtNum(p.matches7d)}</td>
                <td className="py-1 text-right">{fmtNum(p.quickMatches7d)}</td>
                <td className="py-1 text-right">{fmtNum(p.friendMatches7d)}</td>
                <td className="py-1 text-right">{fmtDate(p.lastMatchAt)}</td>
                <td className="py-1 text-right text-white/60">
                  {p.suspiciousQuickRepeatFlag ? 'Repeated QM signal' : p.extremeFriendRepeatFlag ? 'Extreme Friend repeat' : '—'}
                </td>
              </tr>
            ))}
            {(!pairs.pairs?.length) && (
              <tr><td colSpan={8} className="text-white/40 py-2 italic">No repeated opponent pairs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Recent trust events */}
      <div data-testid="trust-recent-events">
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">RECENT TRUST EVENTS</h3>
        <table className="w-full text-sm">
          <thead className="text-white/40 text-[10px] uppercase tracking-[0.22em]">
            <tr>
              <th className="text-left py-1">Time</th>
              <th className="text-left py-1">Event</th>
              <th className="text-left py-1">Player</th>
              <th className="text-left py-1">Opponent</th>
              <th className="text-left py-1">Type</th>
              <th className="text-left py-1">Metadata</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {(overview.recentTrustEvents || []).map((e, i) => (
              <tr key={i} className="border-t border-white/5">
                <td className="py-1">{fmtDate(e.timestamp)}</td>
                <td className="py-1">{e.eventName}</td>
                <td className="py-1">{e.displayName || <span className="text-white/40">{e.guestId?.slice(0, 10)}</span>}</td>
                <td className="py-1 text-white/60">{e.metadata?.opponentGuestId?.slice(0, 10) || '—'}</td>
                <td className="py-1">{e.matchType || '—'}</td>
                <td className="py-1 text-white/60 truncate max-w-[240px]">{JSON.stringify(e.metadata || {})}</td>
              </tr>
            ))}
            {(!overview.recentTrustEvents?.length) && (
              <tr><td colSpan={6} className="text-white/40 py-2 italic">No recent trust events.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Admin Tools */}
      <div data-testid="trust-admin-tools" className="pt-4 border-t border-white/10">
        <h3 className="font-heading text-white/70 tracking-[0.24em] text-xs mb-2">ADMIN TOOLS</h3>
        <button
          data-testid="trust-rebuild-btn"
          onClick={rebuild}
          disabled={rebuildBusy}
          className="px-3 py-1.5 rounded bg-white/[0.06] text-white/80 text-sm hover:bg-white/[0.1] disabled:opacity-50"
        >
          {rebuildBusy ? 'Rebuilding…' : 'Rebuild Trust Signals'}
        </button>
        {rebuildResult && (
          <div data-testid="trust-rebuild-result" className="mt-2 text-white/60 text-xs font-mono">
            {rebuildResult.error
              ? <span className="text-[hsl(var(--k-red))]">Error: {rebuildResult.error}</span>
              : <span>Rebuilt: {rebuildResult.matchesProcessed} matches / {rebuildResult.playersProcessed} players / {rebuildResult.pairsProcessed} pairs / {rebuildResult.eventsProcessed} events.</span>}
          </div>
        )}
      </div>

      <div data-testid="trust-generated-at" className="text-white/30 text-[10px]">
        Generated: {fmtDate(overview.generatedAt)}
      </div>
    </div>
  );
}

// ============================================================
// ROOT

// ============================================================
// PHASE 4.0.10 — Ranked tab (read-only)
// ============================================================
function RankedTab({ apiKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    setErr(''); setData(null);
    adminFetch('/api/admin/ranked/analytics', apiKey)
      .then(setData)
      .catch((e) => setErr(String(e.message)));
  }, [apiKey]);

  if (err) return <div data-testid="ranked-tab-error" className="text-[hsl(var(--k-red))] text-sm">{err}</div>;
  if (!data) return <div className="text-white/50 text-sm">Loading…</div>;

  const s = data.status || {};
  const c = data.counts || {};
  const h = data.health || {};
  const top = data.topPlayers || [];
  const recent = data.recentResults || [];
  const rates = data.rates;

  const dupCount = h.duplicateGroupsCount ?? 0;
  const idxOk = h.uniqRoomEngineIndexExists === true;

  return (
    <div data-testid="ranked-tab" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RankedCard title="Active Season">
          {s.activeSeasonExists ? (
            <div className="text-sm space-y-1" data-testid="ranked-active-season">
              <div><span className="text-white/50">Name:</span> {s.activeSeason?.name}</div>
              <div><span className="text-white/50">ID:</span> <span className="font-mono">{s.activeSeason?.seasonId}</span></div>
              <div><span className="text-white/50">Status:</span> {s.activeSeason?.status}</div>
              <div><span className="text-white/50">Rules v:</span> {s.rulesVersion}</div>
            </div>
          ) : (
            <div className="text-white/60 text-sm" data-testid="ranked-no-active-season">No active season</div>
          )}
        </RankedCard>
        <RankedCard title="System">
          <div className="text-sm space-y-1" data-testid="ranked-system">
            <div><span className="text-white/50">ENABLE_RANKED:</span> {String(s.enableRanked)}</div>
            <div><span className="text-white/50">Server time:</span> <span className="font-mono text-xs">{data.serverTimeUtc}</span></div>
          </div>
        </RankedCard>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ranked-counts">
        {[
          ['Results', c.ranked_match_results],
          ['Player states', c.ranked_player_state],
          ['History (ranked)', c.match_history_ranked_for_season],
          ['Queue', c.ranked_queue],
          ['Matchmaking', c.ranked_matchmaking_queue],
        ].map(([lbl, val]) => (
          <div key={lbl} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-center">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">{lbl}</div>
            <div className="text-2xl font-heading mt-1">{val ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RankedCard title="Unique index">
          <div className={`text-sm ${idxOk ? 'text-emerald-300' : 'text-red-300'}`} data-testid="ranked-index-health">
            {idxOk ? 'uniq_roomCode_engineMatchId — present' : `MISSING — ${String(h.uniqRoomEngineIndexExists)}`}
          </div>
        </RankedCard>
        <RankedCard title="Duplicate history groups">
          <div className={`text-sm ${dupCount === 0 ? 'text-emerald-300' : 'text-red-300'}`} data-testid="ranked-dup-health">
            {dupCount === 0 ? '0 duplicate groups' : `${dupCount} duplicate group(s) detected`}
          </div>
          {Array.isArray(h.duplicateGroupsSample) && h.duplicateGroupsSample.length > 0 && (
            <pre className="mt-2 text-[11px] text-white/60 overflow-x-auto">
              {JSON.stringify(h.duplicateGroupsSample.slice(0, 3), null, 2)}
            </pre>
          )}
        </RankedCard>
      </div>

      {rates && (
        <RankedCard title="Rates">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-testid="ranked-rates">
            <div><div className="text-white/50 text-xs uppercase tracking-[0.22em]">Total results</div><div>{rates.totalRankedResults}</div></div>
            <div><div className="text-white/50 text-xs uppercase tracking-[0.22em]">Penalty rate</div><div>{rates.penaltyRate == null ? '—' : `${(rates.penaltyRate * 100).toFixed(1)}%`}</div></div>
            <div><div className="text-white/50 text-xs uppercase tracking-[0.22em]">Forfeit rate</div><div>{rates.forfeitRate == null ? '—' : `${(rates.forfeitRate * 100).toFixed(1)}%`}</div></div>
            <div><div className="text-white/50 text-xs uppercase tracking-[0.22em]">Avg queue wait</div><div>{rates.averageQueueWaitMs == null ? '— (not tracked)' : `${rates.averageQueueWaitMs} ms`}</div></div>
          </div>
        </RankedCard>
      )}

      <RankedCard title={`Top players (${top.length})`}>
        {top.length === 0 ? (
          <div className="text-white/50 text-sm">No player states yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ranked-top-players">
              <thead className="text-white/50 text-xs uppercase tracking-[0.22em]">
                <tr>
                  <th className="text-left py-2">#</th>
                  <th className="text-left">Player</th>
                  <th className="text-right">SR</th>
                  <th className="text-left pl-4">Tier</th>
                  <th className="text-right">W</th>
                  <th className="text-right">L</th>
                  <th className="text-right">Played</th>
                  <th className="text-right">Placement</th>
                  <th className="text-right">Streak</th>
                </tr>
              </thead>
              <tbody>
                {top.map((p, i) => (
                  <tr key={p.guestId} className="border-t border-white/5">
                    <td className="py-1.5 text-white/50">{i + 1}</td>
                    <td>{p.displayName || <span className="font-mono text-white/60 text-xs">{p.guestId}</span>}</td>
                    <td className="text-right font-mono">{p.currentSR}</td>
                    <td className="pl-4 text-white/70">{p.label}</td>
                    <td className="text-right">{p.wins}</td>
                    <td className="text-right">{p.losses}</td>
                    <td className="text-right">{p.rankedMatchesPlayed}</td>
                    <td className="text-right">{p.placementMatchesPlayed}/10</td>
                    <td className="text-right">{p.currentStreak}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </RankedCard>

      <RankedCard title={`Recent ranked results (${recent.length})`}>
        {recent.length === 0 ? (
          <div className="text-white/50 text-sm">No ranked matches yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ranked-recent-results">
              <thead className="text-white/50 text-xs uppercase tracking-[0.22em]">
                <tr>
                  <th className="text-left py-2">When</th>
                  <th className="text-left">Match</th>
                  <th className="text-left">Players</th>
                  <th className="text-right">P1 Δ</th>
                  <th className="text-right">P2 Δ</th>
                  <th className="text-left pl-4">Ended</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.matchId} className="border-t border-white/5">
                    <td className="py-1.5 text-white/60 text-xs font-mono">{r.processedAt}</td>
                    <td className="font-mono text-xs text-white/60">{r.matchId?.slice(0, 12)}…</td>
                    <td>
                      <span className={r.winnerGuestId === r.p1GuestId ? 'text-emerald-300' : ''}>{r.p1DisplayName || r.p1GuestId}</span>
                      <span className="text-white/40 px-1">vs</span>
                      <span className={r.winnerGuestId === r.p2GuestId ? 'text-emerald-300' : ''}>{r.p2DisplayName || r.p2GuestId}</span>
                    </td>
                    <td className={`text-right font-mono ${r.p1SRDelta > 0 ? 'text-emerald-300' : r.p1SRDelta < 0 ? 'text-red-300' : ''}`}>{r.p1SRDelta > 0 ? `+${r.p1SRDelta}` : r.p1SRDelta}</td>
                    <td className={`text-right font-mono ${r.p2SRDelta > 0 ? 'text-emerald-300' : r.p2SRDelta < 0 ? 'text-red-300' : ''}`}>{r.p2SRDelta > 0 ? `+${r.p2SRDelta}` : r.p2SRDelta}</td>
                    <td className="pl-4 text-white/60 text-xs">{r.endedBy}{r.wentToPenalties ? ' · pens' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </RankedCard>
    </div>
  );
}

function RankedCard({ title, children }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
      <div className="text-white/50 text-xs uppercase tracking-[0.28em] mb-2">{title}</div>
      {children}
    </div>
  );
}

// ============================================================
export default function AdminAnalyticsScreen() {
  const [apiKey, setApiKey] = useState(() => {
    try { return sessionStorage.getItem(KEY_STORAGE) || ''; } catch { return ''; }
  });
  const [tab, setTab] = useState('overview');
  const [openPlayerId, setOpenPlayerId] = useState(null);

  const logout = useCallback(() => {
    try { sessionStorage.removeItem(KEY_STORAGE); } catch (_) { /* ignore */ }
    setApiKey('');
    setOpenPlayerId(null);
  }, []);

  const activeView = useMemo(() => {
    if (openPlayerId) return <PlayerDetail apiKey={apiKey} guestId={openPlayerId} onBack={() => setOpenPlayerId(null)} />;
    switch (tab) {
      case 'players': return <PlayersTab apiKey={apiKey} onOpenPlayer={setOpenPlayerId} />;
      case 'sessions': return <SessionsTab apiKey={apiKey} />;
      case 'quick-match': return <QuickMatchTab apiKey={apiKey} />;
      case 'retention': return <RetentionTab apiKey={apiKey} />;
      case 'recent-events': return <RecentEventsTab apiKey={apiKey} />;
      case 'trust': return <TrustTab apiKey={apiKey} />;
      case 'ranked': return <RankedTab apiKey={apiKey} />;
      default: return <OverviewTab apiKey={apiKey} />;
    }
  }, [apiKey, tab, openPlayerId]);

  if (!apiKey) {
    return <KeyGate onUnlock={setApiKey} />;
  }

  return (
    <div data-testid="admin-analytics-root" className="min-h-[100dvh] w-full bg-black text-white px-4 md:px-8 py-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading text-xl tracking-[0.3em] text-[hsl(var(--k-gold-bright))]">KICKARD · ADMIN ANALYTICS</h1>
            <p className="text-white/40 text-xs mt-1">Internal — do not share this URL.</p>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="text-white/50 text-xs underline">
            Log out
          </button>
        </div>
        <TabBar current={tab} onChange={(t) => { setTab(t); setOpenPlayerId(null); }} />
        <div className="rounded-lg bg-white/[0.02] border border-white/10 p-4 md:p-6">
          {activeView}
        </div>
      </div>
    </div>
  );
}
